import { useState, useRef, useEffect } from 'react'
import { useHead } from '@unhead/react'
import OrchardSpacing from '@/components/calculators/OrchardSpacing'
import FertilizerNutrient from '@/components/calculators/FertilizerNutrient'
import YieldRevenue from '@/components/calculators/YieldRevenue'
import BrixRipeness from '@/components/calculators/BrixRipeness'
import Sidebar from '@/components/layout/Sidebar'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'

export type TabId = 'spacing' | 'fertilizer' | 'yield' | 'brix'
export type CalcTab = TabId

export const TAB_CONFIG: { id: TabId; label: string; icon: string; shortLabel: string }[] = [
  { id: 'spacing', label: 'Orchard Spacing & Population', shortLabel: 'Spacing', icon: '🌳' },
  { id: 'fertilizer', label: 'Fertilizer & Nutrient Diagnostic', shortLabel: 'Fertilizer', icon: '🧪' },
  { id: 'yield', label: 'Crop Yield & Revenue Forecaster', shortLabel: 'Yield', icon: '📊' },
  { id: 'brix', label: 'Brix & Ripeness Engine', shortLabel: 'Brix', icon: '🍋' },
]

function Home() {
  const [activeTab, setActiveTab] = useState<TabId>('spacing')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const mainRef = useRef<HTMLDivElement>(null)

  const activeConfig = TAB_CONFIG.find((t) => t.id === activeTab) ?? TAB_CONFIG[0]

  useHead({
    title: `${activeConfig.label} — Mango Calculator Hub | mangocalculator.com`,
    meta: [
      {
        name: 'description',
        content:
          'Free professional mango calculators: mango tree yield calculator, mango fertilizer calculator, mango orchard spacing calculator, and mango brix calculator. Trusted by farmers, agronomists & food processors worldwide.',
      },
      { name: 'keywords', content: 'mango tree yield calculator, mango fertilizer calculator, mango orchard spacing calculator, mango brix calculator, mango maturity index, ultra high density mango plantation' },
    ],
  })

  // Scroll the calculator panel back to top whenever the active tab changes,
  // so switching calculators always feels like a fresh, deliberate view.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [activeTab])

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab)
    setSidebarOpen(false)
  }

  const renderCalculator = () => {
    switch (activeTab) {
      case 'spacing': return <OrchardSpacing />
      case 'fertilizer': return <FertilizerNutrient />
      case 'yield': return <YieldRevenue />
      case 'brix': return <BrixRipeness />
      default: return <OrchardSpacing />
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#fffbf2', fontFamily: "'Inter', sans-serif" }}>
      <Header
        activeTab={activeTab}
        mobileOpen={sidebarOpen}
        setMobileOpen={setSidebarOpen}
      />

      <div className="flex flex-1">
        <Sidebar
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          mobileOpen={sidebarOpen}
          setMobileOpen={setSidebarOpen}
        />

        <main
          ref={mainRef}
          id="main-content"
          role="main"
          aria-live="polite"
          className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 overflow-y-auto"
        >
          <div className="max-w-5xl mx-auto">
            {renderCalculator()}
          </div>
        </main>
      </div>

      <Footer />
    </div>
  )
}

export default Home
